import { ContentState, convertToRaw } from 'draft-js';
import { format, formatISO } from 'date-fns';
import type { Question, QuestionResponse } from '../types/questionnaire';
import { newQuestionTypes } from 'lib/enums/questionTypes';

/**
 * Prepares a single question response for the backend API
 * Transforms frontend form values to the expected backend format
 */
export const prepareQuestionResponseData = (
  question: Question,
  answer: any
): Partial<QuestionResponse> => {
  switch (question.questionType.toLowerCase()) {
    case newQuestionTypes.media: {
      return {};
    }

    case newQuestionTypes.singleChoice:
    case newQuestionTypes.multipleChoice:
    case newQuestionTypes.choice: {
      return {
        ids: Array.isArray(answer) ? answer : answer ? [answer] : [],
      };
    }

    case newQuestionTypes.openQuestion: {
      // FIX: Use proper Draft.js ContentState instead of hardcoded template
      // This fixes the bug where user text was being lost
      const contentState = ContentState.createFromText(
        answer?.toString() || ''
      );
      const rawContent = convertToRaw(contentState);
      return {
        value: JSON.stringify(rawContent),
      };
    }

    case newQuestionTypes.numeric: {
      return {
        value: parseFloat(String(answer ?? 0)),
      };
    }

    case newQuestionTypes.scale: {
      return {
        value: answer,
      };
    }

    case newQuestionTypes.likert: {
      return {
        answers: answer,
      };
    }

    case newQuestionTypes.date: {
      if (question.mode === 'DEFAULT') {
        if (question.includeTime) {
          return {
            value: formatISO(answer),
          };
        } else {
          return {
            value: formatISO(answer),
          };
        }
      } else if (question.mode === 'COGNITIVE_TEST') {
        return {
          valueCognitiveTest: {
            ...answer,
            year: format(answer.year, 'yyyy'),
          },
        };
      } else {
        throw new Error(`date question mode ${question.mode} not supported`);
      }
    }

    default:
      return { value: answer };
  }
};

/**
 * Prepares all question responses from form values
 * Used when submitting the entire questionnaire
 */
export default function prepareData(values: {
  answer: any[];
  questionnaire: any;
}): QuestionResponse[] {
  const formData: QuestionResponse[] = [];
  const { answer, questionnaire } = values;
  const questions: Question[] = JSON.parse(questionnaire.questionsJson || '[]');

  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    if (!question) continue;

    if (
      answer[i] !== undefined ||
      answer[i] === 0 ||
      question.questionType === 'media_item'
    ) {
      formData.push({
        questionId: question.id || question.questionId || '',
        ...prepareQuestionResponseData(question, answer[i]),
      } as QuestionResponse);
    }
  }

  return formData;
}
